import React, { useMemo, useState } from "react";
import { superService, requestFlow } from "../../services";
import { CUSTOM_MIN_RATE, CUSTOM_STATUS, API_ACCESS_KIND, API_ACCESS_STATUS, expectedQuoteAmount } from "../../config/custom-request";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, Input, Modal, PageHead, Select, Textarea, baht, formatDate, useAsync } from "../../components/ui";

const statusOf = (row) => (row.kind === API_ACCESS_KIND ? API_ACCESS_STATUS : CUSTOM_STATUS)[row.status] || { th: row.status, en: row.status, tone: "gray" };

// คำขอทุกร้าน: ฟีเจอร์ custom / ฟีเจอร์เสริมแบบใบเสนอราคา / บริการ / ขอใบเสนอราคา Ultimate / ขอเปิด API
export default function SuperRequests() {
  const { profile, toast } = useApp();
  const { tx } = useI18n();
  const by = profile?.email || "super_user";
  const { data: requests, loading, reload } = useAsync(() => superService.requests(), [], []);
  const [status, setStatus] = useState("pending_quote");
  const [quoting, setQuoting] = useState(null);
  const [busyId, setBusyId] = useState("");

  const rows = useMemo(() => (requests || []).filter((row) => !status || row.status === status), [requests, status]);

  const changeStatus = async (row, next) => {
    if (next === "cancelled" && !window.confirm(tx(`ยกเลิกคำขอ ${row.requestNo || row.id}?`, `Cancel request ${row.requestNo || row.id}?`))) return;
    setBusyId(row.id);
    try {
      await superService.setRequestStatus(row, next, by);
      reload();
      toast(tx("อัปเดตสถานะแล้ว", "Status updated"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <PageHead title={tx("คำขอ & ใบเสนอราคา", "Requests & quotes")} subtitle={tx("ใส่ใบเสนอราคาแล้วร้านจ่าย QR ได้ทันที · ชำระแล้วเปลี่ยนเป็นกำลังดำเนินการ/เสร็จสิ้น", "Post a quote and the shop can pay by QR · move paid work to in progress / done")} />

      <div className="u-mb-16" style={{ maxWidth: 260 }}>
        <Select
          label={tx("สถานะ", "Status")}
          placeholder={tx("ทุกสถานะ", "All")}
          value={status}
          options={Object.entries(CUSTOM_STATUS).map(([value, label]) => ({ value, label: `${tx(label.th, label.en)} (${(requests || []).filter((row) => row.status === value).length})` }))}
          onChange={(event) => setStatus(event.target.value)}
        />
      </div>

      <DataTable
        loading={loading}
        rows={rows}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "orgName", label: tx("ร้าน", "Shop") },
          {
            key: "title",
            label: tx("คำขอ", "Request"),
            render: (row) => (
              <div style={{ maxWidth: 360 }}>
                <div className="u-strong">{row.title}</div>
                <div className="u-fs-12 u-muted">{row.requestNo} · {row.description}</div>
                {row.contact ? <div className="u-fs-12 u-muted">{tx("ติดต่อ", "Contact")}: {row.contact}</div> : null}
                {(row.links || []).map((link) => <div key={link} className="u-fs-12"><a href={link} target="_blank" rel="noreferrer">{link}</a></div>)}
              </div>
            ),
          },
          { key: "amount", label: tx("ใบเสนอราคา", "Quote"), align: "right", render: (row) => (row.amount ? `${baht(row.amount, 0)} (${row.mandays} × ${baht(row.ratePerManday, 0)})` : "-") },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <Badge tone={statusOf(row).tone}>{tx(statusOf(row).th, statusOf(row).en)}</Badge> },
          {
            key: "action",
            label: "",
            align: "right",
            render: (row) => (
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                {row.kind !== API_ACCESS_KIND && ["pending_quote", "quoted"].includes(row.status) ? (
                  <Button size="sm" variant="primary" onClick={() => setQuoting(row)}>{row.status === "quoted" ? tx("แก้ใบเสนอราคา", "Edit quote") : tx("ใส่ใบเสนอราคา", "Quote")}</Button>
                ) : null}
                {requestFlow(row).map((next) => (
                  <Button key={next} size="sm" disabled={busyId === row.id} onClick={() => changeStatus(row, next)}>
                    {tx(statusOf({ ...row, status: next }).th, statusOf({ ...row, status: next }).en)}
                  </Button>
                ))}
              </div>
            ),
          },
        ]}
        emptyText={tx("ไม่มีคำขอในสถานะนี้", "No requests with this status")}
      />

      {quoting ? (
        <QuoteModal
          row={quoting}
          by={by}
          onClose={() => setQuoting(null)}
          onSaved={() => {
            setQuoting(null);
            reload();
            toast(tx("ส่งใบเสนอราคาแล้ว ร้านจ่ายได้ที่แท็บฟีเจอร์ custom", "Quote posted — the shop can pay in the Custom features tab"), "success");
          }}
        />
      ) : null}
    </>
  );
}

function QuoteModal({ row, by, onClose, onSaved }) {
  const { tx } = useI18n();
  const { toast } = useApp();
  const [form, setForm] = useState({ mandays: row.mandays || "", rate: row.ratePerManday || CUSTOM_MIN_RATE, note: row.quoteNote || "" });
  const [saving, setSaving] = useState(false);
  const amount = Number(form.mandays) > 0 && Number(form.rate) > 0 ? expectedQuoteAmount(form.mandays, form.rate) : 0;

  const save = async () => {
    setSaving(true);
    try {
      await superService.quote(row, form, by);
      onSaved();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={tx(`ใบเสนอราคา: ${row.title}`, `Quote: ${row.title}`)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{tx("ยกเลิก", "Cancel")}</Button>
          <Button variant="primary" disabled={saving || !amount} onClick={save}>{saving ? tx("กำลังบันทึก...", "Saving...") : tx("ส่งใบเสนอราคา", "Post quote")}</Button>
        </>
      }
    >
      <div className="u-fs-13 u-muted u-mb-16">{row.orgName} · {row.requestNo}</div>
      <div className="grid-2" style={{ gap: 12 }}>
        <Input label={tx("จำนวน manday", "Mandays")} type="number" min="0" step="0.25" value={form.mandays} onChange={(event) => setForm({ ...form, mandays: event.target.value })} />
        <Input label={tx(`อัตรา/manday (ขั้นต่ำ ${CUSTOM_MIN_RATE.toLocaleString()})`, `Rate/manday (min ${CUSTOM_MIN_RATE.toLocaleString()})`)} type="number" min={CUSTOM_MIN_RATE} value={form.rate} onChange={(event) => setForm({ ...form, rate: event.target.value })} />
      </div>
      <Textarea label={tx("หมายเหตุถึงร้าน", "Note to shop")} rows={3} value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
      <div className="u-between">
        <span className="u-muted">{tx("ยอดสุทธิ (ไม่รวม VAT)", "Total (excl. VAT)")}</span>
        <span className="u-strong" style={{ fontSize: 22 }}>{baht(amount, 0)}</span>
      </div>
    </Modal>
  );
}
